import React, { useState } from 'react';
import { useModals, MODAL_TYPE } from '../../hooks/useModal';
import {
    Grid2,
    Typography,
    Tooltip,
    IconButton,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    MenuItem,
    Button,
    CircularProgress,
} from '@mui/material';
import {
    useGetBookings,
    getBookings,
    editBookingCheckin,
    addBooking,
} from '@openapi/bookings/bookings';
import { useGetTimeslot } from '@openapi/timeslots/timeslots';
import { useSlotTemplate } from '../../hooks/useSlotTemplate';
import useNotify from '../../hooks/useNotify';
import { useCapabilities } from '../../hooks/useCapabilities';
import useSorting from '../../hooks/useSorting';
const CHECKIN_PAGE_SIZE = 1000;
const CHECKIN_ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 1000];
import PageWithTitle from '../../components/atom/PageWithTitle';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import CommonButton from '../../components/atom/CommonButton';
import DefaultSearchInput from '../../components/atom/DefaultSearchInput';
import CheckinViewTable from './components/CheckinViewTable';
import { formatDateTimeInVenueTz } from '../../helpers/formatDateTime';
import ClockIcon from '@mui/icons-material/AccessTime';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import { collectAllData, exportToXlsx } from '../../helpers/excelExportHelpers';
import { editTimeslot } from '@openapi/timeslots/timeslots';
import CheckinStats from './components/CheckinStats';

const CheckinViewPage = () => {
    const { showModal } = useModals();
    const { t } = useTranslation();
    const { notifyError, notifySuccess } = useNotify();
    const { can, CAPABILITIES } = useCapabilities();
    const canFinishCheckin = can(CAPABILITIES.CHECKIN_FINISH);
    const canManageBookings = can(CAPABILITIES.BOOKINGS_MANAGE);
    const navigate = useNavigate();
    const { timeslotId } = useParams();
    const [{ limit, page }, setParams] = useState({ limit: CHECKIN_PAGE_SIZE, page: 0 });
    const { sort, sortDir, setSorting } = useSorting(['last_name', 'ASC']);
    const [search, setSearch] = useState('');
    const [checkinFilter, setCheckinFilter] = useState(0);
    const [generatingXlsx, setGeneratingXlsx] = useState(false);
    const [progress, setProgress] = useState(0);
    const [checkinSubmitting, setCheckinSubmitting] = useState(false);
    const [addingBooking, setAddingBooking] = useState(false);
    const [groupByAgeBand, setGroupByAgeBand] = useState(false);

    const {
        data,
        isLoading: loading,
        _error,
        refetch: reloadData,
    } = useGetBookings(
        {
            optionalId: timeslotId,
            search,
            page,
            pageSize: limit,
            sort,
            sortDir,
            checkinState: checkinFilter === 0 ? undefined : checkinFilter,
            statusFilter: 'finished,processing',
            // Include bookings from overlapping rental slots because those
            // guests occupy the same physical seats during this interval.
            includeRentalSiblings: true,
            // the check-in page never renders the revenue/persons summary
            includeSummary: false,
        },
        {
            query: {
                onError: notifyError,
            },
        },
    );

    const {
        data: timeslotInfo,
        error: _timeslotError,
        refetch: reloadTimeslot,
    } = useGetTimeslot(Number(timeslotId), {
        query: {
            onError: notifyError,
        },
    });

    const { template: slotTemplate } = useSlotTemplate(timeslotInfo?.showId);

    const isCheckinOpen = !timeslotInfo?.isCheckinClosed;

    // Bookings with no items (created, then all tickets removed before any
    // were re-added) leak into the check-in list because they share the
    // GetBookings endpoint with the main bookings page. Hide them here —
    // the page already loads up to CHECKIN_PAGE_SIZE bookings, so client-
    // side filtering is safe.
    const hiddenTicketlessCount =
        data?.bookings?.filter((b) => (b.ticketTypes?.length ?? 0) === 0).length ?? 0;
    // Overlapping sibling rows arrive first from the backend (getBookings orders
    // them ahead of the requested sort when includeRentalSiblings expands).
    const visibleBookings = data?.bookings?.filter((b) => (b.ticketTypes?.length ?? 0) > 0);
    const visibleTotal =
        data?.total != null ? Math.max(0, data.total - hiddenTicketlessCount) : data?.total;

    // rowTimeslotId: overlapping sibling rows live on another timeslot, and the
    // backend's check-in timeslot proof expects the booking's own slot id.
    const saveCheckinNote = async (bookingId, checkinNote, rowTimeslotId) => {
        try {
            await editBookingCheckin(
                bookingId,
                { checkinNote },
                { timeslotId: rowTimeslotId ?? (timeslotId || undefined) },
            );
            notifySuccess(t('bookingitems.notifications.checkinNote.updated'));
            reloadData();
        } catch (err) {
            notifyError(err);
            if (err?.response?.status === 409) {
                reloadTimeslot();
            }
        }
    };

    const handleClear = () => {
        setSearch('');
        setCheckinFilter(0);
    };

    const handleChangeTimeslot = () => {
        navigate('/checkinview');
    };

    const handleFinishCheckin = () => {
        showModal(MODAL_TYPE.CHECKIN_FINISH, {
            title: t('confirmationModal.title'),
            initialNote: timeslotInfo?.checkinNote ?? '',
            initialSuppressReviewEmail: timeslotInfo?.isReviewEmailSuppressed ?? false,
            confirm: async (note, suppressReviewEmail) => {
                setCheckinSubmitting(true);
                try {
                    await editTimeslot(timeslotId, {
                        showId: timeslotInfo.showId,
                        startTime: timeslotInfo.startTime,
                        checkinNote: note,
                        isCheckinClosed: true,
                        isReviewEmailSuppressed: suppressReviewEmail,
                    });
                    await reloadTimeslot();
                    notifySuccess(t('checkinview.notifications.closed'));
                } catch (err) {
                    notifyError(err);
                } finally {
                    setCheckinSubmitting(false);
                }
            },
        });
    };

    const handleAddBooking = async () => {
        setAddingBooking(true);
        try {
            const res = await addBooking({
                timeslotId: parseInt(timeslotId, 10),
                status: 'processing',
                firstName: '',
                lastName: '',
                phone: '',
                email: '',
                note: [],
                checkinNote: '',
            });
            navigate(`/bookings/edit/${res.id}`);
        } catch (err) {
            notifyError(err);
        } finally {
            setAddingBooking(false);
        }
    };

    const handleReopenCheckin = () => {
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('confirmationModal.title'),
            confirm: async () => {
                setCheckinSubmitting(true);
                try {
                    await editTimeslot(timeslotId, {
                        showId: timeslotInfo.showId,
                        startTime: timeslotInfo.startTime,
                        isCheckinClosed: false,
                    });
                    await reloadTimeslot();
                    notifySuccess(t('checkinview.notifications.reopened'));
                } catch (err) {
                    notifyError(err);
                } finally {
                    setCheckinSubmitting(false);
                }
            },
        });
    };

    return (
        <PageWithTitle
            title={`${t('bookingitems.checkinview')}`}
            headline={[
                timeslotInfo && (
                    <Grid2
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        size={{ xs: 12 }}
                        key={timeslotInfo.id}
                    >
                        <Grid2>
                            <Typography variant="body1">
                                {formatDateTimeInVenueTz(timeslotInfo.startTime)}
                                <br />
                                <small>{timeslotInfo.showTitle}</small>
                            </Typography>
                        </Grid2>
                        <Grid2>
                            <Tooltip title={t('timeslots.changeTimeslot')}>
                                <IconButton onClick={handleChangeTimeslot}>
                                    <ClockIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid2>
                    </Grid2>
                ),
            ]}
        >
            <Grid2
                container
                size={{ xs: 12 }}
                spacing={1}
                direction="row"
                alignItems="flex-end"
                justifyContent="flex-end"
            >
                <Grid2
                    size={{
                        xs: 12,
                        md: canFinishCheckin
                            ? canManageBookings
                                ? 4
                                : 5
                            : canManageBookings
                              ? 6
                              : 7,
                    }}
                >
                    <DefaultSearchInput
                        search={search}
                        setSearch={setSearch}
                        setParams={setParams}
                        limit={limit}
                        fullWidth
                    />
                </Grid2>
                <Grid2 size={{ xs: 6, md: 3 }}>
                    <FormControl fullWidth>
                        <InputLabel id="filter-by">{t('bookings.checkinStatus')}</InputLabel>
                        <Select
                            fullWidth
                            labelId="filter-by"
                            value={checkinFilter}
                            size="small"
                            label={t('bookings.checkinStatus')}
                            onChange={(e) => setCheckinFilter(e.target.value)}
                        >
                            {[
                                { value: 0, label: t('common.all') },
                                { value: 'yes', label: t('common.yes') },
                                { value: 'no', label: t('common.no') },
                            ].map(({ value, label }) => (
                                <MenuItem value={value} key={value}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid2>
                <Grid2 size={{ xs: 6, md: 1 }}>
                    <CommonButton
                        onClick={handleClear}
                        fullWidth
                        label={t('common.clear')}
                        buttonType="text"
                    />
                </Grid2>
                {canFinishCheckin && (
                    <Grid2 size={{ xs: 12, md: 2 }}>
                        <Button
                            fullWidth
                            disabled={checkinSubmitting}
                            onClick={isCheckinOpen ? handleFinishCheckin : handleReopenCheckin}
                        >
                            {checkinSubmitting ? (
                                <CircularProgress size={22} color="inherit" />
                            ) : isCheckinOpen ? (
                                t('checkinview.finishButton')
                            ) : (
                                t('checkinview.reopenButton')
                            )}
                        </Button>
                    </Grid2>
                )}
                {canManageBookings && (
                    <Grid2 size={{ xs: 3, md: 1 }}>
                        <Tooltip title={t('bookings.addBooking')}>
                            <Button fullWidth disabled={addingBooking} onClick={handleAddBooking}>
                                {addingBooking ? (
                                    <CircularProgress size={22} color="inherit" />
                                ) : (
                                    <AddIcon />
                                )}
                            </Button>
                        </Tooltip>
                    </Grid2>
                )}
                <Grid2 size={{ xs: 3, md: 1 }}>
                    <Tooltip title={t('common.export')}>
                        <Button
                            fullWidth
                            disabled={generatingXlsx}
                            onClick={async () => {
                                setGeneratingXlsx(true);
                                let listToExport = [];
                                try {
                                    listToExport = await collectAllData({
                                        data,
                                        apiFunction: (page, pageSize) => {
                                            return getBookings({
                                                optionalId: timeslotId,
                                                checkinState:
                                                    checkinFilter === 0 ? undefined : checkinFilter,
                                                statusFilter: 'finished,processing',
                                                includeRentalSiblings: true,
                                                includeSummary: false,
                                                search,
                                                page,
                                                pageSize,
                                            });
                                        },
                                        setProgress,
                                        responseData: 'bookings',
                                        mappingName: 'bookings',
                                        // Mirror the on-screen filter so the export matches what
                                        // the agent sees on the check-in table. Runs before
                                        // customMapping strips ticketTypes from the row.
                                        filterFn: (b) => (b.ticketTypes?.length ?? 0) > 0,
                                    });
                                    exportToXlsx(listToExport, 'checkinview');
                                    notifySuccess(t('bookings.notifications.exportSuccess'));
                                } catch (err) {
                                    notifyError(err);
                                }
                                setGeneratingXlsx(false);
                                setProgress(0);
                            }}
                            label={t('common.export')}
                        >
                            {generatingXlsx ? (
                                <CircularProgress
                                    variant="determinate"
                                    color="inherit"
                                    size={25}
                                    value={progress}
                                />
                            ) : (
                                <FileDownloadIcon />
                            )}
                        </Button>
                    </Tooltip>
                </Grid2>
            </Grid2>
            <Grid2
                size={{ xs: 12 }}
                sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}
            >
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={groupByAgeBand}
                            onChange={(e) => setGroupByAgeBand(e.target.checked)}
                            size="small"
                        />
                    }
                    label={t('checkinview.groupByAgeBand')}
                />
            </Grid2>
            {!isCheckinOpen && timeslotInfo?.checkinStats && (
                <CheckinStats
                    stats={timeslotInfo.checkinStats}
                    note={timeslotInfo.checkinNote}
                    suppressReviewEmail={timeslotInfo.isReviewEmailSuppressed}
                />
            )}
            <CheckinViewTable
                rows={visibleBookings}
                timeslotId={timeslotId}
                limit={limit}
                page={page}
                setParams={setParams}
                total={visibleTotal}
                loading={loading}
                sort={sort}
                sortDir={sortDir}
                setSorting={setSorting}
                reloadData={reloadData}
                reloadTimeslot={reloadTimeslot}
                saveCheckinNote={saveCheckinNote}
                disabled={!isCheckinOpen}
                groupByAgeBand={groupByAgeBand}
                rowsPerPageOptions={CHECKIN_ROWS_PER_PAGE_OPTIONS}
                slotTemplate={slotTemplate}
            />
        </PageWithTitle>
    );
};

export default CheckinViewPage;
